import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Calendar, MapPin, Trophy, Phone, WhatsappLogo, CheckCircle, ArrowLeft, Clock } from "@phosphor-icons/react"
import { useState } from "react"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"

export function CompetitionRegistrationPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "nigeria",
    age: "",
    position: "",
    experience: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Contact details based on country
  const contactDetails = {
    nigeria: {
      whatsapp: "+234 813 509 1808",
      phoneDisplay: "+234 813 509 1808",
      email: "nigeria@zoeventures.one",
      location: "Suleja Township Stadium, Nigeria",
      contactName: "Nigeria Office"
    },
    india: {
      whatsapp: "+91 73489 58352",
      phoneDisplay: "+91 73489 58352",
      email: "india@zoeventures.one",
      location: "India Office",
      contactName: "India Office"
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          formType: 'Competition Registration'
        })
      })

      if (!response.ok) throw new Error('Failed to send')

      toast.success("Registration submitted successfully! We'll contact you soon.")

      setFormData({
        name: "",
        email: "",
        phone: "",
        country: "nigeria",
        age: "",
        position: "",
        experience: ""
      })
    } catch {
      toast.error("Failed to submit registration. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedCountry = formData.country as keyof typeof contactDetails
  const currentContact = selectedCountry ? contactDetails[selectedCountry] : null

  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory via-ivory to-muted pt-20">
      {/* Past Event Banner */}
      <div className="bg-navy px-6 py-3 flex items-center justify-center gap-3">
        <div className="w-2 h-2 rounded-full bg-ivory/40" />
        <span className="text-ivory/60 text-sm uppercase tracking-wider font-medium">
          Past Event — This competition has concluded
        </span>
      </div>

      {/* Hero Banner */}
      <div className="relative h-80 lg:h-96 overflow-hidden">
        <img 
          src="/images/hero-bg.jpg" 
          alt="Football stadium" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/50 to-ivory" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-4 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 text-gold text-sm font-medium uppercase tracking-wider mb-4 backdrop-blur-sm border border-gold/30">
              <Trophy className="w-4 h-4" weight="fill" />
              ZOE Football Academy Presents
            </div>
            <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-semibold text-ivory mb-4 drop-shadow-lg">
              2-Day Football Scouting Competition
            </h1>
            <p className="text-lg md:text-xl text-ivory/90 max-w-2xl mx-auto">
              Showcase your talent. Win ₦50,000. Launch your professional career.
            </p>
          </div>
        </div>
      </div>

      <div className="pb-20 px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-navy-muted hover:text-navy transition-colors mb-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>

          {/* Event Info Bar - Elegant Horizontal Layout */}
          <div className="bg-white rounded-2xl shadow-xl border border-gold/20 p-6 mb-10">
            <div className="grid md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-gold/10">
              {/* Date */}
              <div className="flex items-start gap-4 pt-4 md:pt-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center flex-shrink-0 border border-gold/20">
                  <Calendar className="w-7 h-7 text-gold" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm text-navy-muted uppercase tracking-wider font-medium mb-1">Date</p>
                  <p className="text-navy font-bold text-lg">2nd & 3rd May 2026</p>
                  <div className="flex items-center gap-2 mt-1 text-navy-muted text-sm">
                    <Clock className="w-3.5 h-3.5" weight="fill" />
                    <span>8:00 AM - 6:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Venue */}
              <div className="flex items-start gap-4 pt-6 md:pt-0 md:pl-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center flex-shrink-0 border border-gold/20">
                  <MapPin className="w-7 h-7 text-gold" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm text-navy-muted uppercase tracking-wider font-medium mb-1">Venue</p>
                  <p className="text-navy font-bold text-lg">Suleja Township Stadium</p>
                  <p className="text-navy-muted text-sm mt-1">Suleja, Nigeria</p>
                </div>
              </div>

              {/* Prize - Highlighted */}
              <div className="flex items-start gap-4 pt-6 md:pt-0 md:pl-6">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold to-gold/80 flex items-center justify-center flex-shrink-0 shadow-lg shadow-gold/30">
                  <Trophy className="w-7 h-7 text-navy" weight="fill" />
                </div>
                <div>
                  <p className="text-sm text-navy-muted uppercase tracking-wider font-medium mb-1">Grand Prize</p>
                  <p className="text-navy font-bold text-2xl">₦50,000</p>
                  <p className="text-gold text-sm font-medium mt-1">Cash + Scouting Opportunities</p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Left Column: Event Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Featured Image with Caption */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src="/images/professional-preparation.jpg" 
                  alt="Professional football pathway" 
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-ivory font-bold text-xl mb-1">Your Gateway to Professional Football</p>
                  <p className="text-ivory/80 text-sm">Elite scouting by certified professional coaches</p>
                </div>
                {/* Badge */}
                <div className="absolute top-4 right-4 bg-gold text-navy px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                  FREE Entry
                </div>
              </div>

              {/* What to Expect */}
              <Card className="border-gold/20 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl text-navy">What to Expect</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    "Professional scouting and evaluation",
                    "Technical skills assessment",
                    "Game intelligence tests",
                    "Physical fitness evaluation",
                    "Networking with coaches and scouts"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-gold w-4 h-4" weight="fill" />
                      </div>
                      <span className="text-navy-muted text-sm">{item}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Assessment Criteria */}
              <Card className="border-gold/20 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl text-navy">Assessment Criteria</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      "Technical ability (ball control, passing, shooting)",
                      "Game intelligence (positioning, decision making)",
                      "Discipline and attitude",
                      "Long-term potential"
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-navy text-ivory flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {idx + 1}
                        </div>
                        <span className="text-navy-muted text-sm pt-0.5">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Sponsorship */}
              <div className="p-6 rounded-2xl bg-gradient-to-br from-navy via-navy-muted to-navy text-ivory shadow-lg">
                <div className="text-center">
                  <p className="text-ivory/50 text-xs uppercase tracking-wider mb-3">Official Sponsor</p>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-ivory/10 rounded-xl flex items-center justify-center border border-gold/30">
                      <img 
                        src="/favicon.svg" 
                        alt="ZOE" 
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                    <span className="text-gold font-bold text-xl">ZOE Football Academy</span>
                  </div>
                  <p className="text-ivory/60 text-sm">Football Without Borders</p>
                </div>
              </div>
            </div>

            {/* Right Column: Registration Form */}
            <div className="lg:col-span-3">
              <Card className="border-gold/20 shadow-xl overflow-hidden">
                <CardHeader className="border-b border-gold/10 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent py-6">
                  <CardTitle className="font-serif text-2xl text-navy">
                    Register for Competition
                  </CardTitle>
                  <p className="text-sm text-navy-muted mt-1">
                    Secure your spot at the scouting event
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Country Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-navy font-semibold text-sm">
                        Country *
                      </Label>
                      <Select 
                        value={formData.country} 
                        onValueChange={(value) => setFormData({ ...formData, country: value })}
                        required
                      >
                        <SelectTrigger id="country" className="border-navy/20 h-12">
                          <SelectValue placeholder="Choose your country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nigeria">Nigeria</SelectItem>
                          <SelectItem value="india">India</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-navy font-semibold text-sm">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="Enter your full name"
                        className="border-navy/20 h-12"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-navy font-semibold text-sm">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          placeholder="your@email.com"
                          className="border-navy/20 h-12"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-navy font-semibold text-sm">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          placeholder={selectedCountry === 'nigeria' ? '+234 XXX XXX XXXX' : '+91 XXXXX XXXXX'}
                          className="border-navy/20 h-12"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="age" className="text-navy font-semibold text-sm">Age Group *</Label>
                        <Select value={formData.age} onValueChange={(value) => setFormData({ ...formData, age: value })} required>
                          <SelectTrigger id="age" className="border-navy/20 h-12">
                            <SelectValue placeholder="Select age" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="13-15">13-15 years</SelectItem>
                            <SelectItem value="16-18">16-18 years</SelectItem>
                            <SelectItem value="18-21">18-21 years</SelectItem>
                            <SelectItem value="21+">21+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="position" className="text-navy font-semibold text-sm">Position *</Label>
                        <Select value={formData.position} onValueChange={(value) => setFormData({ ...formData, position: value })} required>
                          <SelectTrigger id="position" className="border-navy/20 h-12">
                            <SelectValue placeholder="Select position" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="goalkeeper">Goalkeeper</SelectItem>
                            <SelectItem value="defender">Defender</SelectItem>
                            <SelectItem value="midfielder">Midfielder</SelectItem>
                            <SelectItem value="forward">Forward</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="experience" className="text-navy font-semibold text-sm">Experience (Optional)</Label>
                      <Textarea
                        id="experience"
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        placeholder="Tell us about your football experience, teams played for, achievements..."
                        rows={3}
                        className="border-navy/20 resize-none"
                      />
                    </div>

                    {/* Dynamic Contact Info */}
                    {currentContact && (
                      <div className="p-5 bg-gradient-to-r from-gold/10 to-gold/5 rounded-xl border border-gold/20">
                        <h4 className="font-semibold text-navy mb-3 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-gold" weight="fill" />
                          Your Local Contact
                        </h4>
                        <div className="space-y-2.5 text-sm">
                          <div className="flex items-center gap-3 text-navy-muted">
                            <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                              <WhatsappLogo className="w-4 h-4 text-green-600" weight="fill" />
                            </div>
                            <a href={`https://wa.me/${currentContact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-gold transition-colors font-medium">
                              {currentContact.phoneDisplay}
                            </a>
                          </div>
                          <div className="flex items-center gap-3 text-navy-muted">
                            <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-gold" weight="fill" />
                            </div>
                            <span className="font-medium">{currentContact.location}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      className="w-full bg-gold text-navy hover:bg-gold/90 font-bold py-6 text-lg shadow-xl shadow-gold/20 rounded-xl"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Complete Registration"}
                    </Button>

                    <p className="text-center text-xs text-navy-muted">
                      No registration fees. Completely FREE to participate.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Bottom Banner */}
          <div className="mt-12 text-center py-10 px-6 rounded-2xl bg-gradient-to-r from-navy via-navy-muted to-navy shadow-xl">
            <p className="text-ivory text-2xl font-serif font-bold mb-2">
              Discipline. Development. Destiny.
            </p>
            <p className="text-ivory/60 text-sm">
              ZOE Football Academy - Football Without Borders
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
